import { FpsLtmId, FpsLtmInfo, ALL_LTMS } from './FpsLtm';

export class FpsLtmPicker {
  private readonly _overlay: HTMLDivElement;
  private readonly _onSelect: (id: FpsLtmId) => void;
  private readonly _escHandler: (e: KeyboardEvent) => void;

  constructor(mount: HTMLElement, onSelect: (id: FpsLtmId) => void) {
    this._onSelect = onSelect;

    // Overlay
    this._overlay = document.createElement('div');
    Object.assign(this._overlay.style, {
      display: 'none',
      position: 'fixed',
      inset: '0',
      zIndex: '5000',
      background: 'rgba(0,0,0,0.82)',
      overflowY: 'auto',
      fontFamily: 'sans-serif',
    });

    // Panel
    const panel = document.createElement('div');
    Object.assign(panel.style, {
      maxWidth: '860px',
      margin: '60px auto',
      padding: '24px',
      background: '#111',
      borderRadius: '12px',
      color: '#fff',
    });

    // Header row
    const header = document.createElement('div');
    Object.assign(header.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '24px',
    });

    const title = document.createElement('h2');
    title.textContent = 'Select Mode';
    Object.assign(title.style, {
      margin: '0',
      fontSize: '1.6rem',
      fontWeight: 'bold',
      color: '#fff',
    });

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    Object.assign(closeBtn.style, {
      background: 'none',
      border: 'none',
      color: '#aaa',
      fontSize: '2rem',
      cursor: 'pointer',
      lineHeight: '1',
      padding: '0 4px',
    });
    closeBtn.addEventListener('click', () => this.hide());

    header.appendChild(title);
    header.appendChild(closeBtn);

    // Grid
    const grid = document.createElement('div');
    Object.assign(grid.style, {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '16px',
    });

    for (const info of ALL_LTMS) {
      grid.appendChild(this._makeCard(info));
    }

    panel.appendChild(header);
    panel.appendChild(grid);
    this._overlay.appendChild(panel);
    mount.appendChild(this._overlay);

    // ESC handler
    this._escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.hide();
    };
  }

  private _makeCard(info: FpsLtmInfo): HTMLDivElement {
    const card = document.createElement('div');
    Object.assign(card.style, {
      display: 'flex',
      flexDirection: 'column',
      background: '#1a1a1a',
      borderRadius: '8px',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'transform 0.15s, box-shadow 0.15s',
      borderLeft: `5px solid ${info.color}`,
      padding: '16px',
      gap: '8px',
    });

    card.addEventListener('mouseenter', () => {
      card.style.transform = 'translateY(-2px)';
      card.style.boxShadow = `0 4px 20px ${info.color}55`;
    });
    card.addEventListener('mouseleave', () => {
      card.style.transform = '';
      card.style.boxShadow = '';
    });
    card.addEventListener('click', () => {
      this._onSelect(info.id);
      this.hide();
    });

    const name = document.createElement('div');
    name.textContent = info.name;
    Object.assign(name.style, {
      fontWeight: 'bold',
      fontSize: '1rem',
      color: '#fff',
    });

    const desc = document.createElement('div');
    desc.textContent = info.description;
    Object.assign(desc.style, {
      fontSize: '0.78rem',
      color: '#aaa',
      lineHeight: '1.4',
    });

    card.appendChild(name);
    card.appendChild(desc);
    return card;
  }

  show(): void {
    this._overlay.style.display = 'block';
    document.addEventListener('keydown', this._escHandler);
  }

  hide(): void {
    this._overlay.style.display = 'none';
    document.removeEventListener('keydown', this._escHandler);
  }

  isVisible(): boolean {
    return this._overlay.style.display !== 'none';
  }
}
